// SEWA XAI — frontend/src/i18n/translations.ts
// Comprehensive multi-language support for global health deployment.
// Languages: English, French, Yoruba (Nigeria), Swahili (East Africa), Arabic, Hindi.

export type Language = 'en' | 'fr' | 'yo' | 'sw' | 'ar' | 'hi';

export const translations = {
  en: {
    dashboard: 'Dashboard',
    scans: 'Scans',
    worklist: 'Clinical Worklist',
    patients: 'Patients',
    analytics: 'Analytics',
    billing: 'Billing',
    settings: 'Settings',
    criticalFinding: 'Critical Finding',
    urgentFinding: 'Urgent Finding',
    normalFinding: 'Normal',
    aiTriageComplete: 'AI Triage Complete',
    reportReady: 'Report Ready',
    viewDetails: 'View Details',
    downloadReport: 'Download Report',
    bookAppointment: 'Book Appointment',
    appointments: 'Appointments',
    referrals: 'Referrals',
    confirmFinding: 'Confirm Finding',
    correctFinding: 'Correct Finding',
    urgencyRoutine: 'Routine',
    urgencyUrgent: 'Urgent',
    urgencyCritical: 'Critical',
    patientPortal: 'Patient Portal',
    accessLog: 'Data Access Log',
    consent: 'Consent Management',
    welcome: 'Welcome back,',
    status: 'Status',
    priority: 'Priority',
    findings: 'Clinical Findings',
    upload: 'Upload Scan',
    signRelease: 'Sign & Release',
    statCritical: 'STAT / CRITICAL',
    overview: 'Overview',
    aiPerformance: 'AI Performance',
    populationHealth: 'Population Health',
    aiMonitoring: 'AI Live Monitor',
    compliance: 'Compliance',
    onboarding: 'Onboarding',
    roadmap: 'Roadmap',
    platform: 'Platform Overview',
    clinicalRules: 'Clinical Rules',
    advancedOrchestration: 'Advanced Orchestrator',
    peerReview: 'Peer Review',
    euAiAct: 'EU AI Act',
    edgeAI: 'Edge AI',
    scanUpload: 'Scan Upload',
    pacsStatus: 'PACS Status',
    procedures: 'Procedures',
    tenantOnboarding: 'Tenant Onboarding',
    longitudinalTracking: 'Longitudinal Tracking',
  },
  fr: {
    dashboard: 'Tableau de bord',
    scans: 'Scanner',
    worklist: 'Liste clinique',
    patients: 'Patients',
    analytics: 'Analytique',
    billing: 'Facturation',
    settings: 'Paramètres',
    criticalFinding: 'Résultat Critique',
    urgentFinding: 'Résultat Urgent',
    normalFinding: 'Normal',
    aiTriageComplete: 'Tri IA Terminé',
    reportReady: 'Rapport Prêt',
    viewDetails: 'Voir Détails',
    downloadReport: 'Télécharger le Rapport',
    bookAppointment: 'Prendre Rendez-vous',
    appointments: 'Rendez-vous',
    referrals: 'Références',
    confirmFinding: 'Confirmer le Résultat',
    correctFinding: 'Corriger le Résultat',
    urgencyRoutine: 'Routine',
    urgencyUrgent: 'Urgent',
    urgencyCritical: 'Critique',
    patientPortal: 'Portail Patient',
    accessLog: 'Journal d\'Accès',
    consent: 'Gestion du Consentement',
    welcome: 'Bon retour,',
    status: 'Statut',
    priority: 'Priorité',
    findings: 'Résultats cliniques',
    upload: 'Télécharger le scan',
    signRelease: 'Signer et publier',
    statCritical: 'URGENT / CRITIQUE',
    overview: 'Aperçu',
    aiPerformance: 'Performance IA',
    populationHealth: 'Santé Publique',
    aiMonitoring: 'Moniteur IA',
    euAiAct: 'Loi UE sur l\'IA',
    edgeAI: 'IA Périphérique',
    scanUpload: 'Téléchargement Scan',
    pacsStatus: 'Statut PACS',
    procedures: 'Procédures',
    tenantOnboarding: 'Intégration Locataire',
    longitudinalTracking: 'Suivi Longitudinal',
  },
  yo: { // Yoruba (Nigeria)
    dashboard: 'Àtẹ-iṣẹ́',
    scans: 'Àyẹ̀wò-fọ́tò',
    worklist: 'Àtòjọ aláìsàn',
    patients: 'Aláìsàn',
    analytics: 'Ìṣirò-iṣẹ́',
    billing: 'Ìsanwó',
    settings: 'Ètò',
    criticalFinding: 'Àbájáde tó le gan-an',
    urgentFinding: 'Àbájáde tó yá kánkán',
    normalFinding: 'Kò sí nǹkan kan',
    aiTriageComplete: 'Ìṣẹ́ IA parí',
    reportReady: 'Ìròyìn ti wà nílẹ̀',
    viewDetails: 'Wo ẹ̀kúnrẹ́rẹ́',
    downloadReport: 'Gba ìròyìn sílẹ̀',
    bookAppointment: 'Dárúkọ sílẹ̀ fún àyẹ̀wò',
    appointments: 'Àyẹ̀wò',
    referrals: 'Ìdarí',
    confirmFinding: 'Fidí àbájáde múlẹ̀',
    correctFinding: 'Tún àbájáde ṣe',
    urgencyRoutine: 'Láìyá',
    urgencyUrgent: 'Yá kánkán',
    urgencyCritical: 'Léwu',
    patientPortal: 'Àyè Aláìsàn',
    accessLog: 'Àtòjọ ìwọlé',
    consent: 'Ètò ìyọ̀ǹda',
    welcome: 'Káàbọ̀ padà,',
    status: 'Iòyìn',
    priority: 'Ìṣaájú',
    findings: 'Àbájáde ìwòsàn',
    upload: 'Gba fọ́tò sókè',
    signRelease: 'Buwọ́lù kí o sì fi ránṣẹ́',
    statCritical: 'KÍÁKÍÁ / LÉWU',
    overview: 'Àkópọ̀',
    aiPerformance: 'Iṣẹ́ IA',
    populationHealth: 'Ìlera Aráìlú',
    aiMonitoring: 'Ìṣọ́ IA',
    compliance: 'Ìtẹ̀lé òfin',
    onboarding: 'Ìmọ̀lára',
    roadmap: 'Ọ̀nà ọjọ́ iwájú',
    platform: 'Àtẹ Platform',
    clinicalRules: 'Awọn Ofin Iwosan',
    advancedOrchestration: 'Oludari Agbayanu',
    peerReview: 'Àyẹ̀wò Ẹgbẹ́',
    euAiAct: 'EU AI Ofin',
    edgeAI: 'IA Alabode',
    scanUpload: 'Ìgbérọ̀ wálé',
    pacsStatus: 'PACS Ìpo',
    procedures: 'Àwọn ìlànà',
    tenantOnboarding: 'Ìmọ̀lára Aáyán',
    longitudinalTracking: 'Ìpàtàkì Gígùn',
  },
  sw: { // Swahili (East Africa)
    dashboard: 'Dashibodi',
    scans: 'Picha za Matibabu',
    worklist: 'Orodha ya Kliniki',
    patients: 'Wagonjwa',
    analytics: 'Uchambuzi',
    billing: 'Malipo',
    settings: 'Mipangilio',
    criticalFinding: 'Matokeo Muhimu Sana',
    urgentFinding: 'Matokeo ya Haraka',
    normalFinding: 'Kawaida',
    aiTriageComplete: 'Uchunguzi wa AI Umekamilika',
    reportReady: 'Ripoti Iko Tayari',
    viewDetails: 'Angalia Maelezo',
    downloadReport: 'Pakua Ripoti',
    bookAppointment: 'Weka Miadi',
    appointments: 'Miadi',
    referrals: 'Rufaa',
    confirmFinding: 'Thibitisha Matokeo',
    correctFinding: 'Sahihisha Matokeo',
    urgencyRoutine: 'Kawaida',
    urgencyUrgent: 'Haraka',
    urgencyCritical: 'Hatari',
    patientPortal: 'Tovuti ya Mgonjwa',
    accessLog: 'Kumbukumbu za Ufikiaji',
    consent: 'Idhini ya Mgonjwa',
    welcome: 'Karibu tena,',
    status: 'Hali',
    priority: 'Kipaumbele',
    findings: 'Matokeo ya Kliniki',
    upload: 'Pakia Kipimo',
    signRelease: 'Saini na Achia',
    statCritical: 'HARAKA / MUHIMU',
    overview: 'Muhtasari',
    aiPerformance: 'Utendaji wa AI',
    populationHealth: 'Afya ya Jamii',
    aiMonitoring: 'Ufuatiliaji wa AI',
    compliance: 'Utekelezaji',
    onboarding: 'Kuingia',
    roadmap: 'Mpango Kazi',
    platform: 'Muhtasari wa Jukwaa',
    clinicalRules: 'Kanuni za Kliniki',
    advancedOrchestration: 'Mratibu wa Juu',
    peerReview: 'Ukaguzi wa Rika',
    euAiAct: 'Sheria ya EU AI',
    edgeAI: 'AI ya Makali',
    scanUpload: 'Pakia Kipimo',
    pacsStatus: 'Hali ya PACS',
    procedures: 'Tarati bu',
    tenantOnboarding: 'Kuingia kwa Mpangaji',
    longitudinalTracking: 'Ufuatiliaji Mrefu',
  },
  ar: {
    dashboard: 'لوحة القيادة',
    scans: 'الفحوصات الطبية',
    worklist: 'قائمة العمل السريري',
    patients: 'المرضى',
    analytics: 'التحليلات',
    billing: 'الفواتير',
    settings: 'الإعدادات',
    criticalFinding: 'نتائج حرجة',
    urgentFinding: 'نتائج عاجلة',
    normalFinding: 'طبيعي',
    aiTriageComplete: 'اكتمل فرز الذكاء الاصطناعي',
    reportReady: 'التقرير جاهز',
    viewDetails: 'عرض التفاصيل',
    downloadReport: 'تحميل التقرير',
    bookAppointment: 'حجز موعد',
    appointments: 'المواعيد',
    referrals: 'الإحالات',
    confirmFinding: 'تأكيد النتائج',
    correctFinding: 'تصحيح النتائج',
    urgencyRoutine: 'روتيني',
    urgencyUrgent: 'عاجل',
    urgencyCritical: 'حرج',
    patientPortal: 'بوابة المريض',
    accessLog: 'سجل الوصول',
    consent: 'إدارة الموافقة',
    welcome: 'مرحباً بعودتك،',
    status: 'الحالة',
    priority: 'الأولوية',
    findings: 'النتائج السريرية',
    upload: 'تحميل الفحص',
    signRelease: 'التوقيع والإصدار',
    statCritical: 'حالة طارئة / حرجة',
    overview: 'نظرة عامة',
    aiPerformance: 'أداء الذكاء الاصطناعي',
    populationHealth: 'صحة السكان',
    aiMonitoring: 'مراقبة الذكاء الاصطناعي',
    compliance: 'الامتثال',
    onboarding: 'تأهيل',
    roadmap: 'خارطة الطريق',
    platform: 'نظرة عامة على المنصة',
    clinicalRules: 'القواعد السريرية',
    advancedOrchestration: 'المنسق المتقدم',
    peerReview: 'المراجعة من الأقران',
    euAiAct: 'قانون الذكاء الاصطناعي للاتحاد الأوروبي',
    edgeAI: 'الذكاء الاصطناعي الحافي',
    scanUpload: 'تحميل الفحص',
    pacsStatus: 'حالة PACS',
    procedures: 'الإجراءات',
    tenantOnboarding: 'تأهيل المستأجر',
    longitudinalTracking: 'التتبع الطولي',
  },
  hi: {
    dashboard: 'डैशबोर्ड',
    scans: 'मेडिकल स्कैन',
    worklist: 'क्लिनिकल कार्यसूची',
    patients: 'मरीज',
    analytics: 'विश्लेषण',
    billing: 'बिलिंग',
    settings: 'सेटिंग्स',
    criticalFinding: 'गंभीर निष्कर्ष',
    urgentFinding: 'त्वरित निष्कर्ष',
    normalFinding: 'सामान्य',
    aiTriageComplete: 'एआई ट्राइएज पूर्ण',
    reportReady: 'रिपोर्ट तैयार है',
    viewDetails: 'विवरण देखें',
    downloadReport: 'रिपोर्ट डाउनलोड करें',
    bookAppointment: 'अपॉइंटमेंट बुक करें',
    appointments: 'अपॉइंटमेंट',
    referrals: 'रेफरल',
    confirmFinding: 'निष्कर्ष की पुष्टि करें',
    correctFinding: 'निष्कर्ष सही करें',
    urgencyRoutine: 'नियमित',
    urgencyUrgent: 'त्वरित',
    urgencyCritical: 'गंभीर',
    patientPortal: 'रोगी पोर्टल',
    accessLog: 'पहुँच लॉग',
    consent: 'सहमति प्रबंधन',
    welcome: 'वापसी पर स्वागत है,',
    status: 'स्थिति',
    priority: 'प्राथमिकता',
    findings: 'क्लिनिकल निष्कर्ष',
    upload: 'स्कैन अपलोड करें',
    signRelease: 'हस्ताक्षर और जारी करें',
    statCritical: 'तत्काल / गंभीर',
    overview: 'अवलोकन',
    aiPerformance: 'एआई प्रदर्शन',
    populationHealth: 'जनसंख्या स्वास्थ्य',
    aiMonitoring: 'एआई निगरानी',
    compliance: 'अनुपालन',
    onboarding: 'शामिल करना',
    roadmap: 'रोडमैप',
    platform: 'प्लेटफ़ॉर्म अवलोकन',
    clinicalRules: 'नैदानिक नियम',
    advancedOrchestration: 'उन्नत आर्केस्ट्रेटर',
    peerReview: 'सहकर्मी समीक्षा',
    euAiAct: 'EU AI अधिनियम',
    edgeAI: 'एज AI',
    scanUpload: 'स्कैन अपलोड',
    pacsStatus: 'PACS स्थिति',
    procedures: 'प्रक्रियाएँ',
    tenantOnboarding: 'किरायेदार शामिल करना',
    longitudinalTracking: 'दीर्घकालिक ट्रैकिंग',
  }
};

export type TranslationKey = keyof typeof translations.en;
